import React from "react";
import { cn } from "../lib/utils";
import Container from "./ui/Container";
import EntryContent from "./ui/EntryContent";
import IconExternal from "./ui/IconExternal";
import IconLocation from "./ui/IconLocation";
import Section from "./ui/Section";
import SectionCardDark from "./ui/SectionCardDark";
import SiteLink from "./ui/SiteLink";
import useGoogleCalendar from "../hooks/useGoogleCalendar";
import Loading from "./ui/Loading";
import EventDate from "./ui/EventDate";
import { CalendarDays, Clock } from "lucide-react";
import Button from "./ui/Button";
import moment from "moment";

type Props = {
  className?: string;
  data: any;
};

const EventsDark = ({ className, data }: Props) => {
  const { events, loading } = useGoogleCalendar();
  const { title, isVisibleBlock, cta } = data;

  return isVisibleBlock && events && !loading ? (
    <Section className={cn("bg-primary", className)}>
      <Container>
        <EntryContent inverted className="prose-headings:text-center">
          <h2>{title}</h2>
        </EntryContent>
        {loading && (
          <div className="flex items-center justify-center py-24 text-white">
            <Loading classNameSvg="w-12 h-12" />
          </div>
        )}

        <div className="mt-section grid gap-6 lg:grid-cols-2">
          {events.map((event) => (
            <SiteLink
              external
              to={event.location}
              className="group"
              key={event.id}
            >
              <SectionCardDark.CardInner
                className="flex h-full w-full flex-col gap-2 !rounded-2xl !border !bg-background/5 !p-4 text-sm text-background group-hover:border-white/50 sm:flex-row sm:gap-8 lg:!p-8"
                key={event.id}
              >
                <div className="mx-auto flex-shrink-0 sm:m-0">
                  <EventDate
                    date={
                      event.start.dateTime
                        ? event.start.dateTime
                        : event.start.date
                    }
                  />
                </div>

                <EntryContent
                  inverted
                  className="flex flex-grow flex-col prose-headings:m-0 prose-headings:mb-2 prose-headings:text-left prose-headings:text-base sm:prose-headings:text-lg"
                >
                  <div className="flex-grow sm:mb-6 lg:mb-2">
                    <h3>{event.summary}</h3>
                  </div>

                  <div className="not-prose flex flex-col items-start gap-4 text-sm sm:flex-row lg:items-end">
                    <div className="flex flex-grow flex-col">
                      {event.start.date !== event.end.date && (
                        <div className="flex items-center gap-1">
                          <CalendarDays className="w-4 flex-shrink-0 text-white" />
                          <span>
                            {moment(event.start.date).format("MMM D")} -{" "}
                            {moment(event.end.date).format("MMM D, YYYY")}
                          </span>
                        </div>
                      )}
                      {event.start.dateTime && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 flex-shrink-0 text-white" />
                          <span>
                            {event.start.dateTime.split("T")[1].substr(0, 5)}-
                            {event.end.dateTime.split("T")[1].substr(0, 5)} (
                            {event.start.timeZone})
                          </span>
                        </div>
                      )}
                      {event.description !== "" && (
                        <div className="flex items-center gap-1">
                          <IconLocation className="w-4 flex-shrink-0 text-white" />
                          <span className="truncate">{event.description}</span>
                        </div>
                      )}
                    </div>
                    <span className="flex flex-row items-center gap-1 self-center text-sm text-white sm:self-end">
                      <span className="underline underline-offset-4 group-hover:decoration-secondary group-hover:decoration-4">
                        RSVP
                      </span>
                      <IconExternal className="flex-shrink-0" />
                    </span>
                  </div>
                </EntryContent>
              </SectionCardDark.CardInner>
            </SiteLink>
          ))}
        </div>
        {cta && (
          <div className="my-12 text-center sm:mb-0">
            <Button
              asChild
              size={"lg"}
              variant={"white"}
              className="px-16 sm:px-8"
            >
              <SiteLink
                to={`${cta.link}`}
                external={cta.isExternal}
                iconExternal={cta.isExternal}
              >
                {cta.label}
              </SiteLink>
            </Button>
          </div>
        )}
      </Container>
    </Section>
  ) : null;
};

export default EventsDark;
